import React from 'react';
import { Spinner } from 'react-bootstrap';
import { TableExportBar } from '../shared/index';
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';

const TH = {
  padding: '8px 12px', fontSize: '0.67rem', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.4, color: '#4a505c',
  background: '#f5f4f2', borderBottom: '2px solid #d4c8bc',
  whiteSpace: 'nowrap', textAlign: 'right',
};
const TD      = { padding: '8px 12px', fontSize: '0.78rem', color: '#333', borderBottom: '1px solid #ede9e4', textAlign: 'right', verticalAlign: 'middle' };
const TD_BOLD = { ...TD, fontWeight: 700, background: '#f0ede8' };
const TD_PCT  = { ...TD, background: '#f7f6f4', color: '#666' };
const TD_PCT_BOLD = { ...TD_BOLD, background: '#e8e6e2', color: '#444' };
const TD_LABEL    = { ...TD, textAlign: 'left', fontWeight: 600, color: '#4a505c', whiteSpace: 'nowrap' };
const TD_LABEL_BOLD = { ...TD_LABEL, fontWeight: 700, background: '#f0ede8' };

const fmt = (n) =>
  Math.round(Number(n) || 0).toLocaleString('fr-FR').replace(/,/g, ' ');

const pct = (num, den) => {
  if (!den || den === 0) return '—';
  return `${Math.round((num / den) * 100)} %`;
};

/* Aplatit un objet projet en 5 lignes pour export */
const buildExportRows = (data) => {
  const rows = [];
  for (const d of data) {
    const { projet_nom, caTotal, titre, nonTitre, totalVente, resteAVendre, totalProjet } = d;
    const DEFS = [
      { statut: 'Lots titrés',    nb: titre.nb,        ca: titre.ca,        av: titre.avance,        rel: titre.reliquat },
      { statut: 'Lots non titrés',nb: nonTitre.nb,     ca: nonTitre.ca,     av: nonTitre.avance,     rel: nonTitre.reliquat },
      { statut: 'Total vente',    nb: totalVente.nb,   ca: totalVente.ca,   av: totalVente.avance,   rel: totalVente.reliquat },
      { statut: 'Reste à vendre', nb: resteAVendre.nb, ca: resteAVendre.ca, av: null,                rel: resteAVendre.ca },
      { statut: 'Total projet',   nb: totalProjet.nb,  ca: totalProjet.ca,  av: totalProjet.avance,  rel: totalProjet.reliquat },
    ];
    for (const r of DEFS) {
      rows.push({
        'Projet':          projet_nom,
        'Statut':          r.statut,
        'Nbre lots':       r.nb,
        'Prix de vente':   Math.round(Number(r.ca) || 0),
        '% vs CA total':   caTotal ? `${Math.round((r.ca / caTotal) * 100)} %` : '—',
        'Avance':          r.av != null ? Math.round(Number(r.av) || 0) : '—',
        '% Avance / CA':   r.av != null ? pct(r.av, r.ca) : '—',
        'Reliquat':        Math.round(Number(r.rel) || 0),
        '% Reliquat / CA': pct(r.rel, r.ca),
      });
    }
  }
  return rows;
};

const SituationTable = ({ data }) => {
  const { projet_nom, caTotal, titre, nonTitre, totalVente, resteAVendre, totalProjet } = data;

  const rows = [
    {
      label: 'Lots titrés', bold: false,
      nb: titre.nb, ca: titre.ca,
      pctVente: pct(titre.ca, caTotal),
      avance: titre.avance, pctAvance: pct(titre.avance, titre.ca),
      reliquat: titre.reliquat, pctReliquat: pct(titre.reliquat, titre.ca),
    },
    {
      label: 'Lots non titrés', bold: false,
      nb: nonTitre.nb, ca: nonTitre.ca,
      pctVente: pct(nonTitre.ca, caTotal),
      avance: nonTitre.avance, pctAvance: pct(nonTitre.avance, nonTitre.ca),
      reliquat: nonTitre.reliquat, pctReliquat: pct(nonTitre.reliquat, nonTitre.ca),
    },
    {
      label: 'Total vente', bold: true,
      nb: totalVente.nb, ca: totalVente.ca,
      pctVente: pct(totalVente.ca, caTotal),
      avance: totalVente.avance, pctAvance: pct(totalVente.avance, totalVente.ca),
      reliquat: totalVente.reliquat, pctReliquat: pct(totalVente.reliquat, totalVente.ca),
    },
    {
      label: 'Reste à vendre', bold: false,
      nb: resteAVendre.nb, ca: resteAVendre.ca,
      pctVente: pct(resteAVendre.ca, caTotal),
      avance: null, pctAvance: '—',
      reliquat: resteAVendre.ca, pctReliquat: '100 %',
    },
    {
      label: 'Total projet', bold: true,
      nb: totalProjet.nb, ca: totalProjet.ca,
      pctVente: '100 %',
      avance: totalProjet.avance, pctAvance: pct(totalProjet.avance, totalProjet.ca),
      reliquat: totalProjet.reliquat, pctReliquat: pct(totalProjet.reliquat, totalProjet.ca),
    },
  ];

  return (
    <div style={{ marginBottom: 28, borderRadius: 8, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '10px 14px', background: '#f5f4f2', borderBottom: '2px solid #d4c8bc' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2d5a2d' }}>
          {projet_nom} — Situation commerciale
        </span>
        <span style={{ fontSize: '0.62rem', color: '#aaa', fontStyle: 'italic' }}>En DHS</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr>
              <th style={{ ...TH, textAlign: 'left' }}>Statut</th>
              <th style={TH}>Nbre lots</th>
              <th style={TH}>Prix de vente</th>
              <th style={{ ...TH, background: '#efefed' }}>% vs CA total</th>
              <th style={TH}>Avance</th>
              <th style={{ ...TH, background: '#efefed' }}>% Avance / CA</th>
              <th style={TH}>Reliquat</th>
              <th style={{ ...TH, background: '#efefed' }}>% Reliquat / CA</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isReste = r.label === 'Reste à vendre';
              const tdL     = r.bold ? TD_LABEL_BOLD : TD_LABEL;
              const tdN     = r.bold ? TD_BOLD : TD;
              const tdP     = r.bold ? TD_PCT_BOLD : TD_PCT;
              return (
                <tr key={r.label}>
                  <td style={tdL}>{r.label}</td>
                  <td style={tdN}>{r.nb}</td>
                  <td style={tdN}>{fmt(r.ca)}</td>
                  <td style={tdP}>{r.pctVente}</td>
                  <td style={tdN}>{isReste ? '—' : fmt(r.avance)}</td>
                  <td style={tdP}>{r.pctAvance}</td>
                  <td style={{ ...tdN, color: r.reliquat > 0 ? '#c62828' : '#aaa' }}>{fmt(r.reliquat)}</td>
                  <td style={tdP}>{r.pctReliquat}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '6px 14px', background: '#faf9f7', borderTop: '1px solid #ede9e4', fontSize: '0.62rem', color: '#aaa', fontStyle: 'italic' }}>
        * Le total projet combine CA réalisé (ventes contractuelles) et CA potentiel (prix catalogue lots invendus).
      </div>
    </div>
  );
};

const SituationCommercialeSection = ({ data = [], loading }) => {
  if (loading) return <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>;
  if (!data.length) return (
    <div style={{ textAlign: 'center', padding: 30, color: '#aaa', fontSize: '0.85rem' }}>
      Sélectionnez un ou plusieurs projets pour afficher la situation commerciale.
    </div>
  );

  const handleExcel = () => {
    const rows = buildExportRows(data);
    exportExcel(rows, `situation_commerciale_${new Date().toISOString().slice(0, 10)}`);
  };

  const handleCopy = () => {
    const rows = buildExportRows(data);
    const { html, plain } = rowsToHtml(rows, 'brown');
    copyHtmlToClipboard(html, plain);
  };

  return (
    <div>
      <TableExportBar onExcel={handleExcel} onCopy={handleCopy} style={{ marginBottom: 10 }} />
      {data.map(d => <SituationTable key={d.projet_id} data={d} />)}
    </div>
  );
};

export default SituationCommercialeSection;
